import App from '@/App.vue';
import router from '@/router';
import i18n from '@/translations';
import { handleRenderError } from '@/domains/common/services/errorHandler';
import { redactSentryBreadcrumb, redactSentrySpan } from '@/domains/common/services/redactSentryPresignedUrls';
import { registerBaseComponents } from '@/domains/common/services/registerBaseComponents';
import { growthBookKey } from '@/growthbookKey';
import { createPinia } from 'pinia';
import { createApp, reactive } from 'vue';
import * as Sentry from '@sentry/vue';

import '@/assets/base.css';
import { GrowthBook } from '@growthbook/growthbook';
import { registerSW } from 'virtual:pwa-register';

const app = createApp(App);
app.config.performance = true;
app.config.errorHandler = (err, instance, info) => {
    handleRenderError(err as Error, instance, info, false);
};

registerBaseComponents(app);

// This event is used to reload the page when a preload error occurs
window.addEventListener('vite:preloadError', () => {
    window.location.reload();
});

const updateSW = registerSW({
    onNeedRefresh() {
        updateSW(true);
        console.info('💡 Nouvelle version dispo !');
    },
    onOfflineReady() {
        console.info('✅ App prête hors-ligne !');
    },
});

Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
            // Additional SDK configuration goes in here, for example:
            maskAllText: true,
            blockAllMedia: true,
        }),
        Sentry.vueIntegration({
            tracingOptions: {
                trackComponents: true,
            },
        }),
    ],
    dataCollection: { userInfo: false },
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/^https:\/\/api.next.podofit.fr/, /^https:\/\/api.app.podofit.fr/],
    beforeBreadcrumb: redactSentryBreadcrumb,
    beforeSendSpan: redactSentrySpan,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
});

const growthBookInstance = reactive(
    new GrowthBook({
        apiHost: 'https://cdn.growthbook.io',
        clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY,
        attributes: {
            // Add user attributes here
        },
        enableDevMode: true, // Optional: Enable the Visual Editor and dev tools
    }),
);

const initializeGrowthBook = async () => {
    try {
        await growthBookInstance.init({ streaming: true });
        return growthBookInstance;
    } catch (e) {
        console.error('Error initializing GrowthBook:', e);
        return null;
    }
};

const gb = await initializeGrowthBook();

app.provide(growthBookKey, gb as GrowthBook | null);
app.use(router);
app.use(i18n);
app.use(createPinia());
app.mount('#app');

// Share the provider type with other components
